import { useEffect, useId, useState, type FormEvent } from "react";
import { Button, Field, TextArea, cx } from "../ui/primitives";

const CATEGORIES = [
	{ id: "request", label: "要望・改善案" },
	{ id: "bug", label: "不具合" },
	{ id: "ux", label: "使いづらさ・UI" },
	{ id: "praise", label: "良かった点" },
	{ id: "other", label: "その他" },
] as const;

/**
 * フッタ「ご意見はこちら」。v1 ではローカル受付（送信完了表示）まで。
 * 本番の集約先は Zerovia / 管理画面側に後続接続する。
 */
export function FeedbackModal({ open, onClose }: { open: boolean; onClose: () => void }) {
	const titleId = useId();
	const [category, setCategory] = useState<(typeof CATEGORIES)[number]["id"]>("request");
	const [body, setBody] = useState("");
	const [sending, setSending] = useState(false);
	const [done, setDone] = useState(false);

	useEffect(() => {
		if (!open) return;
		setDone(false);
		setBody("");
		setCategory("request");
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open) return null;

	async function onSubmit(event: FormEvent) {
		event.preventDefault();
		if (!body.trim()) return;
		setSending(true);
		await new Promise((resolve) => setTimeout(resolve, 350));
		setSending(false);
		setDone(true);
	}

	return (
		<div className="ag-fb-backdrop" role="presentation" onClick={onClose}>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className="ag-fb-modal"
				onClick={(event) => event.stopPropagation()}
			>
				<div className="ag-fb-head">
					<h2 id={titleId}>ご意見はこちら</h2>
					<button type="button" className="ag-icon-button" aria-label="閉じる" onClick={onClose}>
						×
					</button>
				</div>
				{done ? (
					<div className="ag-fb-body">
						<p>ありがとうございます。内容を受け付けました。</p>
						<Button type="button" className="mt-4" onClick={onClose}>
							閉じる
						</Button>
					</div>
				) : (
					<form className="ag-fb-body" onSubmit={onSubmit}>
						<p className="ag-fb-lead">
							うまくいかなかった点・期待と違った点があれば教えてください。サービス改善に使います。
						</p>
						<Field label="カテゴリ">
							<select
								className={cx(
									"w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-[13.5px] text-ink-900",
								)}
								value={category}
								onChange={(event) =>
									setCategory(event.target.value as (typeof CATEGORIES)[number]["id"])
								}
							>
								{CATEGORIES.map((item) => (
									<option key={item.id} value={item.id}>
										{item.label}
									</option>
								))}
							</select>
						</Field>
						<Field label="内容">
							<TextArea
								required
								rows={5}
								value={body}
								placeholder="できるだけ具体的に書いてください"
								onChange={(event) => setBody(event.target.value)}
							/>
						</Field>
						<div className="mt-4 flex justify-end gap-2">
							<Button type="button" variant="ghost" onClick={onClose}>
								キャンセル
							</Button>
							<Button type="submit" disabled={sending || !body.trim()}>
								{sending ? "送信中…" : "送信する"}
							</Button>
						</div>
					</form>
				)}
			</div>
		</div>
	);
}
